import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../api/axiosInstance';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [mediaStats, setMediaStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAiStatus, setOpenAiStatus] = useState(null);
  const [openAiLoading, setOpenAiLoading] = useState(false);

  useEffect(() => {
    if (user?.permissionLevel === 'admin') {
      fetchMediaStatistics();
      fetchOpenAiStatus();
    }
  }, [user]);

  const fetchOpenAiStatus = async () => {
    try {
      setOpenAiLoading(true);
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/auth/admin/openai-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOpenAiStatus(response.data);
    } catch (err) {
      setOpenAiStatus({ success: false, errorMessage: err.response?.data?.message || err.message });
    } finally {
      setOpenAiLoading(false);
    }
  };

  const fetchMediaStatistics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axiosInstance.get('/media-files/admin/statistics', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setMediaStats(response.data);
    } catch (err) {
      console.error('Error fetching media statistics:', err);
      setError('Error fetching media statistics: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (user?.permissionLevel !== 'admin') {
    return (
      <div className="admin-dashboard">
        <h1>Access Denied</h1>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <h1>Media Statistics Dashboard</h1>
        <p>Loading statistics...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Media Statistics Dashboard</h1>
      
      {error && (
        <div style={{ color: 'red', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Refresh Button */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button 
          onClick={fetchMediaStatistics}
          style={{
            backgroundColor: '#17a2b8',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Refresh Statistics
        </button>
        <button
          onClick={fetchOpenAiStatus}
          disabled={openAiLoading}
          style={{
            backgroundColor: '#6f42c1',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: openAiLoading ? 'not-allowed' : 'pointer',
            opacity: openAiLoading ? 0.7 : 1,
          }}
        >
          {openAiLoading ? 'Checking…' : 'Check OpenAI Quota'}
        </button>
      </div>

      {/* OpenAI Status Card */}
      {(() => {
        const s = openAiStatus;
        if (!s && !openAiLoading) return null;
        if (openAiLoading && !s) return (
          <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <strong>OpenAI API Status</strong> — checking…
          </div>
        );

        const statusMeta = {
          active:         { bg: '#e8f5e9', border: '#a5d6a7', badge: '#2e7d32', label: '✓ Active' },
          quota_exceeded: { bg: '#ffebee', border: '#ef9a9a', badge: '#c62828', label: '✗ Quota Exceeded' },
          invalid_key:    { bg: '#fff3e0', border: '#ffcc80', badge: '#e65100', label: '⚠ Invalid Key' },
          no_key:         { bg: '#f3e5f5', border: '#ce93d8', badge: '#6a1b9a', label: '— No Key Configured' },
          error:          { bg: '#fff8e1', border: '#ffe082', badge: '#f57f17', label: '⚠ Check Error' },
          unknown:        { bg: '#f5f5f5', border: '#e0e0e0', badge: '#616161', label: '? Unknown' },
        };
        const meta = statusMeta[s.status] || statusMeta.unknown;
        const billing = s.billing || {};
        const currentMonthUsd = billing.currentMonthUsageUsd != null ? Number(billing.currentMonthUsageUsd) : null;
        const usedPct = billing.hardLimitUsd && currentMonthUsd != null
          ? Math.min(100, (currentMonthUsd / Number(billing.hardLimitUsd)) * 100)
          : null;
        const barColor = usedPct == null ? '#90caf9'
          : usedPct > 90 ? '#e53935'
          : usedPct > 70 ? '#fb8c00'
          : '#43a047';

        return (
          <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: meta.bg, borderRadius: '8px', border: `1px solid ${meta.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: 12 }}>
              <strong style={{ fontSize: '16px' }}>OpenAI API Status</strong>
              <span style={{ padding: '4px 12px', borderRadius: '12px', backgroundColor: meta.badge, color: '#fff', fontSize: '13px', fontWeight: 600 }}>
                {meta.label}
              </span>
            </div>

            {s.errorMessage && (
              <div style={{ marginBottom: 10, fontSize: '13px', color: '#b71c1c' }}>{s.errorMessage}</div>
            )}

            {/* Credit balance — not available via API; link to OpenAI billing page */}
            <div style={{ marginBottom: 14, padding: '10px 14px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #90caf9', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ color: '#1565c0' }}>Credit balance is not available via the API.</span>
              <a href="https://platform.openai.com/account/billing/overview" target="_blank" rel="noreferrer"
                style={{ padding: '5px 14px', backgroundColor: '#1565c0', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: 600 }}>
                View Balance on OpenAI ↗
              </a>
            </div>

            {billing.available ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: 12 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Used this month</div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
                      ${currentMonthUsd != null ? currentMonthUsd.toFixed(4) : '—'}
                    </div>
                  </div>
                  {billing.remainingUsd != null && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remaining</div>
                      <div style={{ fontSize: '22px', fontWeight: 'bold', color: Number(billing.remainingUsd) < 2 ? '#c62828' : 'inherit' }}>
                        ${Number(billing.remainingUsd).toFixed(2)}
                      </div>
                    </div>
                  )}
                  {billing.hardLimitUsd != null && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hard limit</div>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>${Number(billing.hardLimitUsd).toFixed(2)}</div>
                    </div>
                  )}
                  {billing.softLimitUsd != null && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Soft limit</div>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>${Number(billing.softLimitUsd).toFixed(2)}</div>
                    </div>
                  )}
                </div>
                {usedPct != null && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: 3 }}>
                      <span>Usage vs hard limit</span>
                      <span>{usedPct.toFixed(1)}%</span>
                    </div>
                    <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                      <div style={{ width: `${usedPct}%`, height: '100%', backgroundColor: barColor, borderRadius: '4px', transition: 'width 0.4s' }} />
                    </div>
                  </div>
                )}
                {/* Daily breakdown for org keys */}
                {(billing.usageSource === 'organization' || billing.usageSource === 'organization_usage') && billing.orgCostsBuckets && billing.orgCostsBuckets.length > 0 && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#555', marginBottom: 6, fontWeight: 600 }}>
                      {billing.usageSource === 'organization' ? 'Daily spend (current month):' : 'Daily token usage (current month):'}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {billing.orgCostsBuckets.map((b, i) => (
                        <div key={i} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#f0f4ff', borderRadius: '4px', border: '1px solid #c5cae9' }}>
                          <span style={{ color: '#555' }}>{b.date}: </span>
                          {b.usd != null ? <strong>${Number(b.usd).toFixed(4)}</strong> : <strong>{b.tokens?.toLocaleString()} tok</strong>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {billing.usageSource === 'organization' && (
                  <div style={{ marginTop: 10, fontSize: '12px', color: '#666' }}>
                    ℹ Quota limits are managed per project in your{' '}
                    <a href="https://platform.openai.com/settings/organization/limits" target="_blank" rel="noreferrer">OpenAI organisation settings</a>.
                    Current spend shown above via the Organization Costs API.
                  </div>
                )}
                {billing.usageSource === 'organization_usage' && (
                  <div style={{ marginTop: 10, fontSize: '12px', color: '#666' }}>
                    ℹ Token usage shown above via Completions Usage API (costs endpoint unavailable).
                  </div>
                )}
              </div>
            ) : s.keyPresent && s.status === 'active' ? (
              <div style={{ fontSize: '13px', color: '#555', marginTop: 4 }}>
                <p style={{ margin: '0 0 8px' }}>
                  Monthly spend data requires the <strong>api.usage.read</strong> scope on the API key.
                  The "All" permissions toggle does <em>not</em> include this — it is a separate
                  <strong> Usage → Read</strong> scope in the API key editor.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  Fix: go to{' '}
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>,
                  create a new key (or edit the existing one) and under <strong>Permissions</strong> enable
                  the <strong>Usage</strong> section → set it to <strong>Read</strong>.
                  Then update the <code>OPENAI_API_KEY</code> env variable in your backend.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  Alternatively, view your usage directly at{' '}
                  <a href="https://platform.openai.com/usage" target="_blank" rel="noreferrer">platform.openai.com/usage</a>.
                </p>
                {billing.costsDebug && (
                  <div style={{ marginTop: 8, padding: '8px 12px', backgroundColor: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                    <strong>Costs API debug:</strong><br />
                    HTTP {billing.costsDebug.status}<br />
                    {billing.costsDebug.url && <><span style={{ color: '#888' }}>URL: </span>{billing.costsDebug.url}<br /></>}
                    {billing.costsDebug.error && <><span style={{ color: '#888' }}>Error: </span>{JSON.stringify(billing.costsDebug.error)}</>}
                    {billing.costsDebug.note && <><span style={{ color: '#888' }}>Note: </span>{billing.costsDebug.note}</>}
                  </div>
                )}
              </div>
            ) : null}

            {/* Rate-limit headers — always shown when available */}
            {s.rateLimits && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#444', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Current Rate Limits (live from API)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                  {s.rateLimits.requests && (() => {
                    const { limit, remaining, resetIn } = s.rateLimits.requests;
                    const pct = limit > 0 ? Math.round((remaining / limit) * 100) : 0;
                    const color = pct < 10 ? '#c62828' : pct < 30 ? '#e65100' : '#2e7d32';
                    return (
                      <div style={{ padding: '10px 12px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>Requests / min</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color }}>{remaining.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400, color: '#888' }}>/ {limit.toLocaleString()}</span></div>
                        <div style={{ backgroundColor: '#e0e0e0', borderRadius: '3px', height: '6px', marginTop: 6, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: '3px' }} />
                        </div>
                        {resetIn && <div style={{ fontSize: '10px', color: '#999', marginTop: 4 }}>Resets in {resetIn}</div>}
                      </div>
                    );
                  })()}
                  {s.rateLimits.tokens && (() => {
                    const { limit, remaining, resetIn } = s.rateLimits.tokens;
                    const pct = limit > 0 ? Math.round((remaining / limit) * 100) : 0;
                    const color = pct < 10 ? '#c62828' : pct < 30 ? '#e65100' : '#2e7d32';
                    return (
                      <div style={{ padding: '10px 12px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>Tokens / min</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color }}>{remaining.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400, color: '#888' }}>/ {limit.toLocaleString()}</span></div>
                        <div style={{ backgroundColor: '#e0e0e0', borderRadius: '3px', height: '6px', marginTop: 6, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: '3px' }} />
                        </div>
                        {resetIn && <div style={{ fontSize: '10px', color: '#999', marginTop: 4 }}>Resets in {resetIn}</div>}
                      </div>
                    );
                  })()}
                  {s.rateLimits.images && (() => {
                    const { limit, remaining } = s.rateLimits.images;
                    const pct = limit > 0 ? Math.round((remaining / limit) * 100) : 0;
                    const color = pct < 10 ? '#c62828' : pct < 30 ? '#e65100' : '#2e7d32';
                    return (
                      <div style={{ padding: '10px 12px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>Images / min</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color }}>{remaining.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400, color: '#888' }}>/ {limit.toLocaleString()}</span></div>
                        <div style={{ backgroundColor: '#e0e0e0', borderRadius: '3px', height: '6px', marginTop: 6, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: '3px' }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ fontSize: '10px', color: '#aaa', marginTop: 6 }}>
                  Rate limits reset every minute. Click "Check OpenAI Quota" to refresh.
                </div>
              </div>
            )}

            {s.checkedAt && (
              <div style={{ marginTop: 8, fontSize: '11px', color: '#888' }}>Last checked: {new Date(s.checkedAt).toLocaleString()}</div>
            )}
          </div>
        );
      })()}

      {/* Media Statistics Section - Always Visible */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0', padding: '20px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          Media Statistics & Analytics
        </h3>
        
        {mediaStats ? (
              <div style={{ padding: '20px' }}>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                  <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#0056b3' }}>Total Media Files</h4>
                    <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold' }}>{mediaStats.summary.totalMediaFiles}</p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#e8f5e8', borderRadius: '8px', border: '1px solid #b3e5b3' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#006600' }}>Total Views</h4>
                    <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold' }}>{mediaStats.summary.totalViews}</p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#e65100' }}>Total Uploads</h4>
                    <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold' }}>{mediaStats.summary.totalUploads}</p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px', border: '1px solid #ce93d8' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#6a1b9a' }}>Recent Activity (30 days)</h4>
                    <p style={{ margin: '0', fontSize: '14px' }}>
                      {mediaStats.summary.recentViews} views, {mediaStats.summary.recentUploads} uploads
                    </p>
                  </div>
                </div>

                {/* MIDI & Link Statistics Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                  <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#495057' }}>MIDI Files</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalMidiFiles || 0}
                    </p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#1565c0' }}>MIDI Views</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalMidiViews || 0}
                    </p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#e8f5e8', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#2e7d32' }}>MIDI Uploads</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalMidiUploads || 0}
                    </p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#f57c00' }}>Total Links</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalLinks || 0}
                    </p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#fce4ec', borderRadius: '8px', border: '1px solid #f8bbd9' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#c2185b' }}>Link Clicks</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalLinkClicks || 0}
                    </p>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: '#e1f5fe', borderRadius: '8px', border: '1px solid #81d4fa' }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#0277bd' }}>Link Creations</h4>
                    <p style={{ margin: '0', fontSize: '20px', fontWeight: 'bold' }}>
                      {mediaStats.summary.totalLinkCreations || 0}
                    </p>
                  </div>
                </div>

                {/* Most Viewed Media */}
                {mediaStats.viewsPerMedia && mediaStats.viewsPerMedia.length > 0 && (
                  <div style={{ marginBottom: '30px' }}>
                    <h4>Most Viewed Media Files</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>File Name</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Type</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Total Views</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Unique Users</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Last Viewed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mediaStats.viewsPerMedia.slice(0, 10).map((item) => (
                            <tr key={item.mediaFileId} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ padding: '12px' }}>{item.mediaFileName}</td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  backgroundColor: item.mediaFileType === 'mp3' ? '#28a745' : '#007bff',
                                  color: 'white'
                                }}>
                                  {item.mediaFileType.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: '12px' }}>{item.viewCount}</td>
                              <td style={{ padding: '12px' }}>{item.uniqueUserCount}</td>
                              <td style={{ padding: '12px' }}>{new Date(item.lastViewed).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Viewers */}
                {mediaStats.viewsPerUser && mediaStats.viewsPerUser.length > 0 && (
                  <div style={{ marginBottom: '30px' }}>
                    <h4>Most Active Viewers</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>User</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Email</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Total Views</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Files Viewed</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Last Activity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mediaStats.viewsPerUser.slice(0, 10).map((item) => (
                            <tr key={item.userId} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ padding: '12px' }}>{item.userName}</td>
                              <td style={{ padding: '12px' }}>{item.userEmail}</td>
                              <td style={{ padding: '12px' }}>{item.viewCount}</td>
                              <td style={{ padding: '12px' }}>{item.uniqueMediaFilesViewed}</td>
                              <td style={{ padding: '12px' }}>{new Date(item.lastViewed).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Uploaders */}
                {mediaStats.uploadsPerUser && mediaStats.uploadsPerUser.length > 0 && (
                  <div style={{ marginBottom: '30px' }}>
                    <h4>Most Active Uploaders</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>User</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Email</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Uploads</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Total Size</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Last Upload</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mediaStats.uploadsPerUser.slice(0, 10).map((item) => (
                            <tr key={item.userId} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ padding: '12px' }}>{item.userName}</td>
                              <td style={{ padding: '12px' }}>{item.userEmail}</td>
                              <td style={{ padding: '12px' }}>{item.uploadCount}</td>
                              <td style={{ padding: '12px' }}>
                                {(item.totalSizeUploaded / 1024 / 1024).toFixed(2)} MB
                              </td>
                              <td style={{ padding: '12px' }}>{new Date(item.lastUploaded).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* File Type and Streaming Method Statistics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  {/* File Type Stats */}
                  {mediaStats.fileTypeStats && mediaStats.fileTypeStats.length > 0 && (
                    <div>
                      <h4>File Types</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Type</th>
                              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Count</th>
                              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Total Size</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mediaStats.fileTypeStats.map((item) => (
                              <tr key={item._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                <td style={{ padding: '8px' }}>
                                  <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    backgroundColor: 
                                      item._id === 'mp3' ? '#28a745' : 
                                      item._id === 'midi' ? '#6f42c1' :
                                      item._id === 'mp4' ? '#fd7e14' :
                                      item._id === 'wav' ? '#20c997' :
                                      '#007bff',
                                    color: 'white'
                                  }}>
                                    {item._id.toUpperCase()}
                                  </span>
                                </td>
                                <td style={{ padding: '8px' }}>{item.count}</td>
                                <td style={{ padding: '8px' }}>
                                  {item.totalSize ? 
                                    `${(item.totalSize / 1024 / 1024).toFixed(2)} MB` : 
                                    'N/A'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Streaming Method Stats */}
                  {mediaStats.streamingMethodStats && mediaStats.streamingMethodStats.length > 0 && (
                    <div>
                      <h4>Streaming Methods</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Method</th>
                              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mediaStats.streamingMethodStats.map((item) => (
                              <tr key={item._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                <td style={{ padding: '8px' }}>
                                  <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    backgroundColor: '#6c757d',
                                    color: 'white'
                                  }}>
                                    {item._id}
                                  </span>
                                </td>
                                <td style={{ padding: '8px' }}>{item.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '20px', fontSize: '12px', color: '#6c757d' }}>
                  Statistics generated at: {new Date(mediaStats.generatedAt).toLocaleString()}
                </div>
              </div>
            ) : (
              <p style={{ padding: '20px', margin: '0' }}>Failed to load statistics.</p>
            )}
          </div>
      </div>
    );
  };

  export default AdminDashboard;
